import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import { OPTIMIZED_ASSIGNED_TAGS_ENDPOINT } from "constants/feature-flags";
import { TagTypes } from "constants/tags";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  AssignedTagHistoryEvent,
  AssignedTagHistoryFilter
} from "./types/assigned-tag-history.type";
import {
  AssignedTag,
  AssignedTagFilter,
  AssignedTagInput,
  AssignedTagResponse
} from "./types/assigned-tag.type";

// SN-19687. Devices, not patients: `assigned-tags/bulk/` tags PATIENTS only. Synchronous and
// all-or-nothing; a pair that already exists is counted in `already_tagged`, not reported as an
// error.
export interface BulkAssignDeviceTagsInput {
  tag_ids: number[];
  patient_product_ids: number[];
}

export interface BulkAssignDeviceTagsResponse {
  created: number;
  already_tagged: number;
}

// SN-19666's inverse. Same body as the assign endpoint; the counts are the mirror image.
// `not_tagged` is the number of requested (tag, device) pairs that were not there — a no-op, not
// an error, the same way `already_tagged` is on the way in.
export interface BulkRemoveDeviceTagsInput {
  tag_ids: number[];
  patient_product_ids: number[];
}

export interface BulkRemoveDeviceTagsResponse {
  removed: number;
  not_tagged: number;
}

export interface BulkAssignTagsInput {
  tag_ids: number[];
  patients_filter: Record<string, any>;
  description?: string;
}

export interface BulkRemoveTagsInput {
  tag_ids: number[];
  patients_filter: Record<string, any>;
  description?: string;
}

export interface TagPresentInFilter {
  id: number;
  name: string;
  color: string;
  count: number;
}

const buildListUrl = (type: string, filter: AssignedTagFilter): string => {
  const { useOptimizedEndpoint, ...rest } = filter;
  const prefix = useOptimizedEndpoint
    ? "assigned-tags-optimized/"
    : "assigned-tags/";
  return `${prefix}?type=${type}&${formatValidQueryParamUrlCommaSeparated(
    rest
  )}&limit=300`;
};

// Each tag type invalidates its own assigned-tags list plus the parent list that
// renders the chips, so the UI reflects add/update/remove after a refetch.
type AssignedTagInvalidationTag =
  | "AssignedConversationTags"
  | "AssignedSaleTags"
  | "Sales"
  | "AssignedPatientChartNoteTags"
  | "AssignedPatientProductTags"
  | "PatientProducts"
  | "AssignedAppointmentTags"
  | "Appointments"
  | "Events"
  | "VisitTrackersAppointments"
  | "AssignedPatientTags"
  | "Patients"
  | "StaffTasks"
  | "InventoryProductDetail"
  | "EraClaims"
  | "Paginated-EraClaims"
  | "InternalDocuments";

const getAssignedTagInvalidations = (
  tag_type: TagTypes
): AssignedTagInvalidationTag[] => {
  if (tag_type === "Sale") {
    return ["AssignedSaleTags", "Sales"];
  } else if (tag_type === "ChartNote") {
    return ["AssignedPatientChartNoteTags"];
  } else if (tag_type === "PatientProduct") {
    return ["AssignedPatientProductTags", "PatientProducts"];
  } else if (tag_type === "Appointment") {
    return [
      "AssignedAppointmentTags",
      "Appointments",
      "Events",
      "VisitTrackersAppointments"
    ];
  } else if (tag_type === "Patient") {
    return ["AssignedPatientTags", "Patients"];
  } else if (tag_type === "Conversation") {
    // Two-layer sync, mirroring mark-as-read (SN-18999): the
    // conversationTagsEvent in inbox-slice applies the change to the dialog
    // list's locally accumulated rows immediately (tag invalidation cannot
    // reach earlier pages), while the "PatientDialogs" invalidation is the
    // server-state repair for the still-subscribed cache entries — without
    // it, the next navigation re-merges the stale cached rows over the
    // event-applied ones. One refetch per user tag click, no storm.
    return ["AssignedConversationTags", "PatientDialogs"];
  } else if (tag_type === "Task") {
    return ["StaffTasks"];
  } else if (tag_type === "ERAClaim") {
    return [];
  } else if (tag_type === "InventoryProduct") {
    // The clinic-stock list and the limit=300 product picker stay correct via
    // the optimistic onTagsChange/updateItem path, so don't refetch them here.
    // Only refresh the single-object detail query so it isn't stale on revisit.
    return ["InventoryProductDetail"];
  } else if (tag_type === "InternalDocument") {
    // SN-20123: an SOP's categories are its tags; the library groups by them.
    return ["InternalDocuments"];
  } else if (tag_type === "Call") {
    // Nothing, on purpose. The call lists are `usePagination` accumulators that
    // are never invalidated (SN-18212), and invalidating one call's detail
    // refetches it, which blanks the detail panel behind a spinner. Rows and
    // detail follow a tag change through the call-tags overlay instead
    // (features/calls/dialstack/call-tags.slice.ts).
    return [];
  }
  return [];
};

export const assignedTagsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("Patient", filter),
      providesTags: ["AssignedPatientTags"]
    }),
    getPatientProductAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("PatientProduct", filter),
      providesTags: ["AssignedPatientProductTags"]
    }),

    getAppointmentAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("Appointment", filter),
      providesTags: ["AssignedAppointmentTags"]
    }),
    getSaleAssignedTags: builder.query<AssignedTagResponse, AssignedTagFilter>({
      query: (filter) => buildListUrl("Sale", filter),
      providesTags: ["AssignedSaleTags"]
    }),

    getPatientChartNoteAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("ChartNote", filter),
      providesTags: ["AssignedPatientChartNoteTags"]
    }),

    getConversationAssignedTags: builder.query<
      AssignedTagResponse,
      AssignedTagFilter
    >({
      query: (filter) => buildListUrl("Conversation", filter),
      providesTags: ["AssignedConversationTags"]
    }),

    addAssignedTag: builder.mutation<AssignedTag, AssignedTagInput>({
      query: ({ tag_type, ...body }) => ({
        url: `assigned-tags/`,
        method: "POST",
        body
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    updateAssignedTag: builder.mutation<
      AssignedTag,
      Partial<AssignedTagInput> & { tag_type: TagTypes; id: number }
    >({
      query: ({ id, tag_type, ...body }) => ({
        url: `assigned-tags/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    removeAssignedTag: builder.mutation<
      AssignedTagResponse,
      { tag: number; tag_type: TagTypes }
    >({
      query: ({ tag }) => ({
        url: `assigned-tags/${tag}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, _, { tag_type }) =>
        getAssignedTagInvalidations(tag_type)
    }),
    bulkAssignDeviceTags: builder.mutation<
      BulkAssignDeviceTagsResponse,
      BulkAssignDeviceTagsInput
    >({
      query: (body) => ({
        url: "assigned-tags/bulk-devices/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        ...getAssignedTagInvalidations(TagTypes.PATIENT_PRODUCT),
        "PatientProductsWithAssignedTags"
      ]
    }),
    // POST, not DELETE: it carries a body of ids, and it mirrors `bulk-devices/` so the two read
    // as one pair. Same invalidations - the chips this removes are rendered from the same caches.
    bulkRemoveDeviceTags: builder.mutation<
      BulkRemoveDeviceTagsResponse,
      BulkRemoveDeviceTagsInput
    >({
      query: (body) => ({
        url: "assigned-tags/bulk-devices-remove/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        ...getAssignedTagInvalidations(TagTypes.PATIENT_PRODUCT),
        "PatientProductsWithAssignedTags"
      ]
    }),
    bulkAssignTags: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      BulkAssignTagsInput
    >({
      query: (body) => ({
        url: "assigned-tags/bulk/",
        method: "POST",
        body
      })
    }),
    // Additional Bulk Actions: the mirror of bulkAssignTags.
    bulkRemoveTags: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      BulkRemoveTagsInput
    >({
      query: (body) => ({
        url: "assigned-tags/bulk-remove/",
        method: "POST",
        body
      })
    }),
    // Patient tags carried by the filtered audience, with counts. POST because the
    // filter blob is the report's whole query, sent exactly as the bulk endpoints get it.
    tagsPresentInFilter: builder.query<
      TagPresentInFilter[],
      { patients_filter: Record<string, any> }
    >({
      query: (body) => ({
        url: "assigned-tags/present-in-filter/",
        method: "POST",
        body
      }),
      providesTags: ["AssignedPatientTags"]
    }),

    // Every tag ever applied to one record — current and removed — with who and when.
    // One endpoint serves all ten tag types: AssignedTag is a single model behind a
    // GenericForeignKey, so `type` + `object_id` is the whole key. `type` is load-bearing,
    // not cosmetic: Conversation and Patient tags share a Patient row.
    //
    // Fetched lazily, only when someone opens the history dialog — never part of any list
    // projection, so no surface that renders tag chips pays for it.
    getAssignedTagHistory: builder.query<
      AssignedTagHistoryEvent[],
      AssignedTagHistoryFilter
    >({
      query: ({ type, object_id }) =>
        `assigned-tags/history/?type=${encodeURIComponent(
          type
        )}&object_id=${object_id}`
    })
  })
});

const {
  useGetPatientAssignedTagsQuery: useGetPatientAssignedTagsRawQuery,
  useLazyGetPatientAssignedTagsQuery: useLazyGetPatientAssignedTagsRawQuery,
  useGetPatientProductAssignedTagsQuery:
    useGetPatientProductAssignedTagsRawQuery,
  useGetAppointmentAssignedTagsQuery: useGetAppointmentAssignedTagsRawQuery,
  useGetSaleAssignedTagsQuery: useGetSaleAssignedTagsRawQuery,
  useGetPatientChartNoteAssignedTagsQuery:
    useGetPatientChartNoteAssignedTagsRawQuery,
  useLazyGetPatientChartNoteAssignedTagsQuery:
    useLazyGetPatientChartNoteAssignedTagsRawQuery,
  useGetConversationAssignedTagsQuery: useGetConversationAssignedTagsRawQuery,
  useAddAssignedTagMutation,
  useUpdateAssignedTagMutation,
  useRemoveAssignedTagMutation,
  useBulkAssignTagsMutation,
  useBulkRemoveTagsMutation,
  useTagsPresentInFilterQuery,
  useBulkAssignDeviceTagsMutation,
  useBulkRemoveDeviceTagsMutation,
  // Needs no optimized-endpoint wrapper — history has a single endpoint.
  useLazyGetAssignedTagHistoryQuery
} = assignedTagsApi;

const useOptimizedAssignedTagsFlag = (): boolean => {
  const flags = useFlags();
  return !!flags[OPTIMIZED_ASSIGNED_TAGS_ENDPOINT];
};

export const useGetPatientAssignedTagsQuery: typeof useGetPatientAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useLazyGetPatientAssignedTagsQuery: typeof useLazyGetPatientAssignedTagsRawQuery =
  (options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    const [trigger, result, lastArg] =
      useLazyGetPatientAssignedTagsRawQuery(options);
    const wrappedTrigger = useCallback(
      (filter, preferCacheValue) =>
        trigger({ ...filter, useOptimizedEndpoint }, preferCacheValue),
      [trigger, useOptimizedEndpoint]
    ) as typeof trigger;
    return [wrappedTrigger, result, lastArg];
  };

export const useGetPatientProductAssignedTagsQuery: typeof useGetPatientProductAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientProductAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetAppointmentAssignedTagsQuery: typeof useGetAppointmentAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetAppointmentAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetSaleAssignedTagsQuery: typeof useGetSaleAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetSaleAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetPatientChartNoteAssignedTagsQuery: typeof useGetPatientChartNoteAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetPatientChartNoteAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useGetConversationAssignedTagsQuery: typeof useGetConversationAssignedTagsRawQuery =
  (filter, options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    return useGetConversationAssignedTagsRawQuery(
      { ...filter, useOptimizedEndpoint },
      options
    );
  };

export const useLazyGetPatientChartNoteAssignedTagsQuery: typeof useLazyGetPatientChartNoteAssignedTagsRawQuery =
  (options) => {
    const useOptimizedEndpoint = useOptimizedAssignedTagsFlag();
    const [trigger, result, lastArg] =
      useLazyGetPatientChartNoteAssignedTagsRawQuery(options);
    const wrappedTrigger = useCallback(
      (filter, preferCacheValue) =>
        trigger({ ...filter, useOptimizedEndpoint }, preferCacheValue),
      [trigger, useOptimizedEndpoint]
    ) as typeof trigger;
    return [wrappedTrigger, result, lastArg];
  };

export {
  useAddAssignedTagMutation,
  useUpdateAssignedTagMutation,
  useRemoveAssignedTagMutation,
  useBulkAssignTagsMutation,
  useBulkRemoveTagsMutation,
  useTagsPresentInFilterQuery,
  useBulkAssignDeviceTagsMutation,
  useBulkRemoveDeviceTagsMutation,
  useLazyGetAssignedTagHistoryQuery
};
